import React, { useState, useEffect } from "react";
import { FaStar } from "react-icons/fa";
import BigRateCard from "./BigRateCard";
import { getPrice, formatPrice } from "../../services/priceUtils";
import { useNavigate } from "react-router-dom";
import { useCurrency } from '../../context/CurrencyContext';
import Swal from "sweetalert2";

const ListCard = ({ index = 0, isFirstHotel, hotel, handleViewDetails, setSelectedRate, checkIn, checkOut, prices, roomTypes = {}, setRoomTypes = () => {}, roomLimits = {}, occupancies = [] }) => {
  console.log('hotel: ', hotel);
  const navigate = useNavigate();
  console.log('list occupancies: ', occupancies);
  const [priceWithMarkup, setPriceWithMarkup] = useState("0.00");
  const { selectedCurrency } = useCurrency();
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [displayedRooms, setDisplayedRooms] = useState([]);
  const [displayedRates, setDisplayedRates] = useState([]);
  const [showAllRates, setShowAllRates] = useState(false);
  
  useEffect(() => {
    const loadPrice = async () => {
      if (hotel?.rooms) {
        const userData = JSON.parse(localStorage.getItem('user_data'));
        if (!userData?.token) return;

        try {
          let lowestRate = Infinity;
          for (const room of hotel.rooms) {
            if (room.rates && room.rates.length > 0) {
              for (const rate of room.rates) {
                const rateWithMarkup = await getPrice(
                  rate.net, 
                  userData.token,
                  selectedCurrency,
                  rate.taxes, 
                  hotel.country?.code,
                  rate.currency
                );
                const numericRate = Number(rateWithMarkup);
                if (!isNaN(numericRate) && numericRate > 0 && numericRate < lowestRate) {
                  lowestRate = numericRate;
                }
              }
            }
          }
          
          if (lowestRate !== Infinity) {
            setPriceWithMarkup(lowestRate.toFixed(2));
          } else {
            const price = await getPrice(
              hotel?.minRate, 
              userData.token,
              selectedCurrency,
              null,
              hotel.country?.code,
              hotel?.currency || 'EUR'
            );
            setPriceWithMarkup(price);
          }
        } catch (error) {
          console.error("Error calculating price:", error);
          const price = await getPrice(
            hotel?.minRate, 
            userData.token,
            selectedCurrency,
            null,
            hotel.country?.code,
            hotel?.currency || 'EUR'
          );
          setPriceWithMarkup(price);
        }
      }
    };
    loadPrice();
  }, [hotel, selectedCurrency]);

  useEffect(() => {
    if (hotel?.rooms) {
      const rooms = hotel.rooms;
      if (rooms.length <= 4) {
        setDisplayedRooms(rooms);
        setShowAllRooms(true);
      } else {
        setDisplayedRooms(rooms.slice(0, 4));
        setShowAllRooms(false);
      }
    }
  }, [hotel]);

  useEffect(() => {
    if (hotel?.rooms) {
      // Tüm odaların rate'lerini birleştir
      const allRates = hotel.rooms.reduce((acc, room) => {
        if (room.rates && room.rates.length > 0) {
          return [...acc, ...room.rates.map(rate => ({
            ...rate,
            roomName: room.name,
            roomCode: room.code
          }))];
        }
        return acc;
      }, []);

      if (allRates.length <= 2) {
        setDisplayedRates(allRates);
        setShowAllRates(true);
      } else {
        setDisplayedRates(allRates.slice(0, 2));
        setShowAllRates(false);
      }
    }
  }, [hotel]);

  // URL oluşturma fonksiyonu
  const generateDetailUrl = () => {
    const roomsParam = occupancies.map(o => o.rooms).join(',');
    const adultsParam = occupancies.map(o => o.adults).join(',');
    const childrenParam = occupancies.map(o => o.children).join(',');
    const agesParam = occupancies.map(o => 
      o.paxes.map(p => p.age).join('-')
    ).join('|');
    const nationalityParam = localStorage.getItem('nationality') || 'DE';
    const checkIn = hotel?.check_in || '';
    const checkOut = hotel?.check_out || '';

    return `/hotel-detail/${hotel.id}/${hotel.slug}?checkIn=${checkIn}&checkOut=${checkOut}&rooms=${roomsParam}&adults=${adultsParam}&children=${childrenParam}&ages=${agesParam}&nationality=${nationalityParam}`;
  };

  const handleClick = () => {
    navigate(generateDetailUrl());
    window.scrollTo(0, 0);
  };

  // Gece sayısını hesaplayan fonksiyon
  const calculateNights = () => {
    // Önce rate içindeki nights değerini kontrol et
    if (hotel?.rooms?.[0]?.rates?.[0]?.nights) {
      return hotel.rooms[0].rates[0].nights;
    }

    // Eğer nights değeri yoksa, mevcut hesaplama yöntemini kullan
    if (hotel?.rooms?.[0]?.rates?.[0]?.rateKey) {
      const dates = hotel.rooms[0].rates[0].rateKey.split('|');
      if (dates.length >= 2) {
        const checkIn = new Date(dates[0].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
        const checkOut = new Date(dates[1].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
        const diffTime = Math.abs(checkOut - checkIn);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }
    }
    return 1; // Varsayılan değer
  };

  const nights = calculateNights();
  const perNightPrice = priceWithMarkup && nights ? (Number(priceWithMarkup) / nights).toFixed(2) : "0.00";

  // Mesafeyi formatla
  const formatDistance = (distance) => {
    if (distance === undefined || distance === null) return "";
    return distance < 1 ? 
      `${(distance * 1000).toFixed(0)} m` : 
      `${distance.toFixed(1)} km`;
  };

  // Hotel name'i güvenli bir şekilde al
  const hotelName = typeof hotel.name === 'object' ? hotel.name?.content || '' : hotel.name || '';

  // Hotel description'ı güvenli bir şekilde al
  const hotelDescription = typeof hotel.description === 'object' ? hotel.description?.content || '' : hotel.description || '';

  // Hotel address'i güvenli bir şekilde al
  const hotelAddress = typeof hotel.address === 'object' ? hotel.address?.content || '' : hotel.address || '';

  // Hotel city'i güvenli bir şekilde al
  const hotelCity = typeof hotel.city === 'object' ? hotel.city?.content || '' : hotel.city || '';

  // Hotel country'yi güvenli bir şekilde al
  const hotelCountry = typeof hotel.country === 'object' ? hotel.country?.description?.content || '' : hotel.country || '';

  // Hotel category'yi güvenli bir şekilde al
  const hotelCategory = typeof hotel.category === 'object' ? hotel.category?.description?.content || '' : hotel.category || '';

  // Hotel accommodation type'ı güvenli bir şekilde al
  const hotelAccommodationType = typeof hotel.accommodationType === 'object' ? hotel.accommodationType?.typeDescription || '' : hotel.accommodationType || '';

  // Hotel boards'ları güvenli bir şekilde al
  const hotelBoards = Array.isArray(hotel.boards) ? hotel.boards.map(board => 
      typeof board === 'object' ? board || '' : board
  ) : [];

  // Hotel facilities'leri güvenli bir şekilde al
  const hotelFacilities = Array.isArray(hotel.facilities) ? hotel.facilities.map(facility => 
      typeof facility === 'object' ? facility.description?.content || '' : facility
  ) : [];

  // Hotel rooms'ları güvenli bir şekilde al
  const hotelRooms = Array.isArray(hotel.rooms) ? hotel.rooms.map(room => ({
      ...room,
      description: typeof room.description === 'object' ? room.description?.content || '' : room.description || '',
      type: typeof room.type === 'object' ? room.type?.description?.content || '' : room.type || '',
      characteristic: typeof room.characteristic === 'object' ? room.characteristic?.description?.content || '' : room.characteristic || ''
  })) : [];

  const handleRoomSelect = (roomType, isSelected) => {
    if (isSelected) {
      // Check if room limit is reached
      if (roomLimits[roomType]?.current >= roomLimits[roomType]?.limit) {
        Swal.fire({
          title: 'Limit Reached',
          text: `You can only select ${roomLimits[roomType]?.limit} ${roomType} room(s)`,
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
    }

    setRoomTypes(prev => {
      const newRoomTypes = { ...prev };
      if (isSelected) {
        newRoomTypes[roomType] = (newRoomTypes[roomType] || 0) + 1;
      } else {
        newRoomTypes[roomType] = Math.max(0, (newRoomTypes[roomType] || 0) - 1);
      }
      return newRoomTypes;
    });
  };

  return (
    <div 
      id={`hotel-card-${hotel.id}`}
      key={index} 
      style={{ boxShadow: "0px 4px 24px 0px rgba(194, 194, 194, 0.25)", marginBottom: "30px" }}
    >
      <div
        className="place-item d-flex flex-column flex-md-row" 
        style={isFirstHotel === true ? { backgroundColor: "lightyellow", borderRadius: "10px 0 0 0" } : (hotel?.isSimilar ? { backgroundColor: "lightyellow", borderRadius: "10px 0 0 0" } : {borderRadius: "10px 0 0 0"})}
      >
        <div className="place-img" style={{ borderRadius: "10px 0 0 0" }}>
          <a 
            href={generateDetailUrl()}
            onClick={(e) => {
              e.preventDefault();
              handleViewDetails(hotel);
            }}
          >
            <img
              style={{ height: "270px", width: "100%", objectFit: "cover", borderRadius: "10px 0 0 0" }}
              src={hotel?.image ?? "/assets/image-4.png"}
              alt="hotel-img"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
          </a>
        </div>
        <div
          style={isFirstHotel === true ? { backgroundColor: "lightyellow", borderRadius: "0 10px 10px 0" } : (hotel?.isSimilar ? { backgroundColor: "lightyellow", borderRadius: "0 10px 10px 0" } : {borderRadius: "0 10px 10px 0"})}
          className="place-content d-flex flex-column justify-content-between pb-1">
          <div className="d-flex align-items-center justify-content-between flex-wrap">
            <div>
              <h5 className="mb-1" style={{ textTransform: "capitalize" }}>
                <a 
                  href={generateDetailUrl()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleViewDetails(hotel);
                  }}
                >
                  {hotelName}
                </a>
                {hotel?.category && /^\d/.test(hotel.category) && (
                  <div className="d-inline-flex align-items-center ms-2">
                    {(() => {
                      const starCount = parseInt(hotel.category.match(/\d+/)?.[0] || '0');
                      return (
                        <>
                          {[...Array(5)].map((_, index) => (
                            <FaStar
                              key={index}
                              style={{ 
                                color: index < starCount ? "orange" : "lightgray",
                                marginRight: index < 4 ? "2px" : "0",
                                fontSize: "14px"
                              }}
                            />
                          ))}
                          <span className="badge badge-warning badge-xs text-gray-9 fs-13 fw-medium ms-2">
                            {starCount}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                )}
                {hotel?.isSimilar && (
                  <span className="badge bg-warning text-dark ms-2">Similar</span>
                )}
              </h5>
              <div className="d-flex align-items-center gap-2 mb-2">
                {hotelAccommodationType && (
                  <span className="badge bg-light text-dark text-capitalize">
                    <i className="isax isax-building me-1"></i>
                    {hotelAccommodationType?.toLowerCase()}
                  </span>
                )}
                {hotelCategory && (
                  <span className="badge bg-light text-dark text-capitalize">
                    <i className="isax isax-tag me-1"></i>
                    {hotelCategory?.toLowerCase()}
                  </span>
                )}
              </div>
              <p className="d-flex align-items-center mb-2">
                <i className="isax isax-location5 me-2"></i>
                <span style={{ textTransform: "capitalize" }}>
                  {hotelCity?.toLowerCase()}, {hotelCountry?.toLowerCase()}
                  {hotel?.distance !== undefined && (
                    <span className="ms-2 text-primary">
                      ({formatDistance(hotel.distance)})
                    </span>
                  )}
                </span>
              </p>
              <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                {hotelBoards && hotelBoards.length > 0 && (
                  <div className="d-flex align-items-center gap-2">
                    {hotelBoards.map((board, index) => (
                      <span key={index} className="badge d-flex align-items-center" style={{ backgroundColor: '#e8f4ff', color: '#0066cc' }}>
                        <i className="isax isax-coffee me-1"></i>
                        {board.code || board}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center mb-2">
            </div>
          </div>
          <p className="line-ellipsis fs-14">
            {hotelDescription}
          </p>
          {hotel?.segments?.length > 0 && (
            <div className="d-flex align-items-center gap-4 mb-3 flex-wrap pt-3">
              {hotel?.segments?.slice(0, 3).map((item, index) => (
                <h6 key={index} className="text-primary text-8">{item.description.content}</h6>
              ))}
            </div>
          )}
          <div className="d-flex flex-row align-items-center justify-content-end border-top flex-wrap pt-3">
            <div className="d-flex flex-column align-items-end me-2">
              <h5 className="text-primary text-nowrap mb-1">
                {priceWithMarkup} {selectedCurrency}
                <span className="fs-14 fw-normal text-default"> / {nights} {nights === 1 ? 'Night' : 'Nights'}</span>
              </h5>
              <small className="text-muted fs-12">
                {(Number(priceWithMarkup) / nights).toFixed(2)} {selectedCurrency} per night
              </small>
            </div>
          </div>
        </div>
      </div>
      <div style={{ backgroundColor: "white", border: "1px solid rgba(164, 167, 174, 0.41)" }}>
        <div className="d-flex flex-column" id="room-contents" style={{ borderRadius: "15px", backgroundColor: "white", padding: "10px" }}>
          {displayedRates?.map((rate, index) => (
            <BigRateCard 
              key={`rate-${index}`}
              rate={rate}
              setSelectedRate={setSelectedRate}
              thumbnail={hotel?.thumbnail}
              roomName={rate.roomName}
              checkIn={checkIn}
              checkOut={checkOut}
              occupancies={occupancies}
            />
          ))} 
          
          {!showAllRates && hotel?.rooms?.reduce((acc, room) => acc + (room.rates?.length || 0), 0) > 2 && (
            <div className="d-flex justify-content-center align-items-center mt-4 mb-4">
              <div 
                className="show-more-text"
                onClick={() => {
                  const allRates = hotel.rooms.reduce((acc, room) => {
                    if (room.rates && room.rates.length > 0) {
                      return [...acc, ...room.rates.map(rate => ({
                        ...rate,
                        roomName: room.name,
                        roomCode: room.code
                      }))];
                    }
                    return acc;
                  }, []);
                  setDisplayedRates(allRates);
                  setShowAllRates(true);
                }}
              >
                <span>Show More Rates</span>
                <i className="isax isax-arrow-down-1"></i>
              </div>
            </div>
          )}
          
          {showAllRates && hotel?.rooms?.reduce((acc, room) => acc + (room.rates?.length || 0), 0) > 2 && (
            <div className="d-flex justify-content-center align-items-center mt-4 mb-4">
              <div 
                className="show-more-text"
                onClick={() => {
                  // Önce scroll işlemini yap
                  const cardElement = document.getElementById(`hotel-card-${hotel.id}`);
                  if (cardElement) {
                    cardElement.scrollIntoView({ 
                      behavior: 'smooth', 
                      block: 'start',
                      inline: 'nearest'
                    });
                  }

                  // Scroll başladıktan sonra accordion'u kapat
                  setTimeout(() => {
                    const allRates = hotel.rooms.reduce((acc, room) => {
                      if (room.rates && room.rates.length > 0) {
                        return [...acc, ...room.rates.map(rate => ({
                          ...rate,
                          roomName: room.name,
                          roomCode: room.code
                        }))];
                      }
                      return acc;
                    }, []);
                    setDisplayedRates(allRates.slice(0, 2));
                    setShowAllRates(false);
                  }, 500); // Scroll animasyonu tamamlanmaya yakın zamanda accordion'u kapat
                }}
              >
                <span>Show Less Rates</span>
                <i className="isax isax-arrow-up-1"></i>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="room-selection">
        {Object.keys(roomLimits || {}).map(roomType => (
          <div key={roomType} className="room-option">
            <input
              type="checkbox"
              id={`room-${roomType}`}
              checked={roomTypes[roomType] > 0}
              onChange={(e) => handleRoomSelect(roomType, e.target.checked)}
              disabled={roomLimits[roomType]?.disabled}
            />
            <label htmlFor={`room-${roomType}`}>
              {roomType} ({roomTypes[roomType] || 0} selected)
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ListCard;

// Global styles
const styles = `
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.room-contents {
    transition: all 0.3s ease-in-out;
}

.show-more-text {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6c757d;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 8px 16px;
    border-radius: 4px;
}

.show-more-text:hover {
    color: #0d6efd;
    background-color: rgba(13, 110, 253, 0.05);
}

.show-more-text i {
    font-size: 16px;
    transition: transform 0.3s ease;
}

.show-more-text:hover i {
    transform: translateY(2px);
}
`;

// Add styles to document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
